import { clsx } from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, ChatColumn } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';
import { getSiteSetupToolCalls, SITE_SETUP_STEPS } from '@services/content-agents/browser';

import { type AgentStateSource, useAgentState } from '../Agent';

type SiteSetupStepStatus = api.AIToolCallSiteSetupStep['status'];

export type SiteSetupStepActions = Partial<Record<api.SiteSetupStepId, React.ReactNode>>;

type SiteSetupStepsContextValue = {
    /** Per step: its latest status in the conversation, and its first call, which draws its card. */
    steps: Partial<
        Record<
            api.SiteSetupStepId,
            { status: SiteSetupStepStatus; first: api.AIToolCallSiteSetupStep }
        >
    >;
    /** Run the action of the current step's card: send its message as the user's. */
    onStepAction?: (message: string) => void;
    /** A step's own action on its card, in place of the one it sends as a message. */
    stepActions: SiteSetupStepActions;
    /** The steps whose card scrolled up into the deck, in order. */
    pinned: api.SiteSetupStepId[];
    setPinned: React.Dispatch<React.SetStateAction<api.SiteSetupStepId[]>>;
};

/** Space above the first card of the deck: its `pt-1`, plus the card's own `my-1`. */
const DECK_TOP = 8;

const SITE_SETUP_STEP_IDS = Object.values(api.SiteSetupStepId);

const SiteSetupStepsContext = React.createContext<SiteSetupStepsContextValue>({
    steps: {},
    stepActions: {},
    pinned: [],
    setPinned: () => {},
});

/**
 * Publish where each setup step stands in the conversation, so a step's card shows how it ended
 * rather than how it started, and which cards scrolled up into the deck. Mounted only around a
 * setup conversation.
 */
export function SiteSetupStepsProvider(
    props: React.PropsWithChildren<{
        agent: AgentStateSource;
        onStepAction?: (message: string) => void;
        stepActions: SiteSetupStepActions;
    }>
) {
    const { agent, onStepAction, stepActions, children } = props;
    const messages = useAgentState(agent, selectAgentMessages);
    const [pinned, setPinned] = React.useState<api.SiteSetupStepId[]>([]);

    const value = React.useMemo(() => {
        const steps: SiteSetupStepsContextValue['steps'] = {};
        for (const toolCall of getSiteSetupToolCalls(messages)) {
            steps[toolCall.step] = {
                status: toolCall.status,
                first: steps[toolCall.step]?.first ?? toolCall,
            };
        }
        return { steps, onStepAction, stepActions, pinned, setPinned };
    }, [messages, onStepAction, stepActions, pinned]);

    return (
        <SiteSetupStepsContext.Provider value={value}>{children}</SiteSetupStepsContext.Provider>
    );
}

/**
 * The step cards scrolled up out of the conversation, stacked at its top: each covers most of the
 * one before, and the deck spreads out while hovered or focused. A card scrolls up over the deck
 * and joins it once it reaches its slot, where the deck draws it in its place.
 */
export function SiteSetupStepsDeck() {
    const { steps, onStepAction, stepActions, pinned, setPinned } =
        React.useContext(SiteSetupStepsContext);
    const [overlap, setOverlap] = React.useState(0);
    const deckRef = React.useRef<HTMLDivElement>(null);

    const update = useEventCallback(() => {
        const deck = deckRef.current;
        // The deck stands first in the conversation's scroll area, at its top.
        const scrollArea = deck?.parentElement;
        // Spread out, the slots are not where the cards join.
        if (!deck || !scrollArea || deck.matches(':hover, :focus-within')) {
            return;
        }
        const cards = [...scrollArea.querySelectorAll<HTMLElement>('[data-site-setup-step]')];
        const height = (cards[0]?.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0;
        // Each card shows a third of the one before: the rest of it is covered, with its
        // `my-1` margins and the deck's `gap-1`.
        const strip = Math.round(height / 3);
        const top = deck.getBoundingClientRect().top + DECK_TOP;
        // In order: a card joins once its top reaches the slot after the cards already in.
        const next: api.SiteSetupStepId[] = [];
        for (const card of cards) {
            const cardTop = card.firstElementChild?.getBoundingClientRect().top ?? Infinity;
            if (cardTop > top + next.length * strip) {
                break;
            }
            next.push(card.dataset.siteSetupStep as api.SiteSetupStepId);
        }
        setOverlap(height + 12 - strip);
        setPinned((previous) => (previous.join() === next.join() ? previous : next));
    });

    React.useEffect(() => {
        const scrollArea = deckRef.current?.parentElement;
        scrollArea?.addEventListener('scroll', update, { passive: true });
        return () => scrollArea?.removeEventListener('scroll', update);
    }, [update]);
    // Also as the conversation changes, which moves the cards without a scroll.
    React.useLayoutEffect(update, [steps, update]);

    return (
        <div
            ref={deckRef}
            className="sticky top-0 z-10 -mb-3 h-0 focus-within:z-30 hover:z-30"
            style={{ '--deck-overlap': `-${overlap}px` } as React.CSSProperties}
            onPointerLeave={update}
        >
            {pinned.length > 0 ? (
                <>
                    <ChatColumn className="group/deck gap-1 bg-base pt-1">
                        {pinned.map((step, index) => (
                            <div
                                key={step}
                                className={
                                    index > 0
                                        ? 'mt-(--deck-overlap) transition-[margin] duration-200 group-focus-within/deck:mt-0 group-hover/deck:mt-0'
                                        : undefined
                                }
                            >
                                <SiteSetupStepRow
                                    step={step}
                                    status={steps[step]?.status ?? 'current'}
                                    onStepAction={onStepAction}
                                    stepAction={stepActions[step]}
                                />
                            </div>
                        ))}
                    </ChatColumn>
                    {/* Fades the conversation out as it slides under the deck. */}
                    <div className="h-6 bg-linear-to-b from-(--sp-color-bg-base) to-transparent" />
                </>
            ) : null}
        </div>
    );
}

/**
 * A setup step in the conversation, with its latest status. One card per step, at its first call.
 */
export function SiteSetupStepCard(props: { toolCall: api.AIToolCallSiteSetupStep }) {
    const { toolCall } = props;
    const { steps, onStepAction, stepActions, pinned } = React.useContext(SiteSetupStepsContext);
    const step = steps[toolCall.step];

    if (step?.first !== toolCall) {
        return null;
    }

    const isPinned = pinned.includes(toolCall.step);

    return (
        // Over the deck until it joins it, then under the copy the deck draws in its place, which
        // is then the only one to focus, press or announce.
        <div
            data-site-setup-step={toolCall.step}
            className={isPinned ? 'relative' : 'relative z-20'}
            inert={isPinned}
        >
            <SiteSetupStepRow
                step={toolCall.step}
                status={step.status}
                onStepAction={onStepAction}
                stepAction={stepActions[toolCall.step]}
            />
        </div>
    );
}

/** The setup step the conversation is on: the last one started and not ended since. */
export function selectCurrentSiteSetupStep(state: {
    messages?: api.AIMessage[];
}): api.SiteSetupStepId | null {
    const last = getSiteSetupToolCalls(state.messages ?? []).at(-1);
    return last?.status === 'current' ? last.step : null;
}

/** The current setup step, until the user answers the message that made it current. */
export function selectUnansweredSiteSetupStep(state: {
    messages?: api.AIMessage[];
}): api.SiteSetupStepId | null {
    const messages = state.messages ?? [];
    const lastUserIndex = messages.findLastIndex(
        (message) => message.role === api.AIMessageRole.User
    );
    return selectCurrentSiteSetupStep({ messages: messages.slice(lastUserIndex + 1) });
}

/**
 * The step just done, while the next one waits for the user to move on: one they may keep working
 * on, which the conversation last showed as done.
 */
export function selectContinuingSiteSetupStep(state: {
    messages?: api.AIMessage[];
}): api.SiteSetupStepId | null {
    const last = getSiteSetupToolCalls(state.messages ?? []).at(-1);
    return last?.status === 'done' && SITE_SETUP_STEPS[last.step].keepEditing ? last.step : null;
}

/** The step the conversation continues on, until the user answers the message that showed it done. */
export function selectUnansweredContinuingSiteSetupStep(state: {
    messages?: api.AIMessage[];
}): api.SiteSetupStepId | null {
    const messages = state.messages ?? [];
    const lastUserIndex = messages.findLastIndex(
        (message) => message.role === api.AIMessageRole.User
    );
    return selectContinuingSiteSetupStep({ messages: messages.slice(lastUserIndex + 1) });
}

/** Whether the conversation has ended the setup: publishing, the last step, is done. */
export function selectSiteSetupEnd(state: { messages?: api.AIMessage[] }): boolean {
    const last = getSiteSetupToolCalls(state.messages ?? []).at(-1);
    return last?.step === api.SiteSetupStepId.Publish && last.status === 'done';
}

/**
 * The conversation's messages, for {@link useAgentState}: the provider then re-renders only when
 * they change, not on every agent state update. Module-level so its identity stays stable.
 */
function selectAgentMessages(state: { messages: api.AIMessage[] }): api.AIMessage[] {
    return state.messages;
}

/** A setup step's card: its number, title and where it stands. */
function SiteSetupStepRow(props: {
    step: api.SiteSetupStepId;
    status: SiteSetupStepStatus;
    onStepAction?: (message: string) => void;
    stepAction: React.ReactNode;
}) {
    const { step, status, onStepAction, stepAction } = props;
    const { title, action } = SITE_SETUP_STEPS[step];

    return (
        <div className="my-1 flex items-center gap-2 rounded-lg border border-neutral-subtle bg-base px-2.5 py-1.5 text-ui-small">
            <SiteSetupStepIcon status={status} />
            <span className="shrink-0 text-muted tabular-nums">
                Step {SITE_SETUP_STEP_IDS.indexOf(step) + 1} of {SITE_SETUP_STEP_IDS.length}
            </span>
            <span
                className={clsx(
                    'min-w-0 flex-1 truncate text-ui-base',
                    status === 'done' ? 'text-muted line-through' : 'text-neutral-high'
                )}
            >
                {title}
            </span>
            {status === 'current' ? (
                (stepAction ??
                (action.kind === 'skip' && onStepAction ? (
                    <Button
                        kind="blank"
                        size="small"
                        label="Skip"
                        onPress={() => onStepAction(action.message)}
                    />
                ) : null))
            ) : status === 'pending' ? null : (
                <span className="shrink-0 text-muted">
                    {status === 'done' ? 'Done' : 'Skipped'}
                </span>
            )}
        </div>
    );
}

/** Same marks as the steps of a `StepProgressCard`, with a dashed circle for a skipped step. */
function SiteSetupStepIcon(props: { status: SiteSetupStepStatus }) {
    switch (props.status) {
        case 'done':
            return (
                <span className="flex size-3.5 shrink-0 items-center justify-center rounded-full bg-success-solid text-inverted">
                    <Icon.Check className="size-2.5" />
                </span>
            );
        case 'current':
            return (
                <span className="flex size-3.5 shrink-0 items-center justify-center rounded-full border-[1.5px] border-neutral-high">
                    <span className="size-1 rounded-full bg-neutral-solid" />
                </span>
            );
        case 'skipped':
            return (
                <span className="size-3.5 shrink-0 rounded-full border-[1.5px] border-dashed border-neutral-subtle" />
            );
        case 'pending':
            return (
                <span className="size-3.5 shrink-0 rounded-full border-[1.5px] border-neutral-subtle" />
            );
        default:
            assertNever(props.status);
    }
}
